// client/src/coms/components/SoundClipEditor.js
// Clip editor used by Settings → Soundboard: import a file or record from the microphone, trim on the
// waveform, preview, name, emoji, description, keywords and volume, then save on this device or into a
// pack. An MP3 (up to 1 MB) going into a pack is uploaded as it is, with the trim range, and converted on
// the server (no quality lost to an extra conversion); recordings and other formats are sent as the WAV
// made here. With `replace` it re-trims an existing sound and keeps its record.
import React, { useEffect, useRef, useState } from 'react';
import { comsApiPatch } from '../comsApi';
import { buildClip, decodeAudioFile, MAX_CLIP_SECONDS, peaks, recordMicrophone, sha256Hex } from '../soundboard/audioPrep';
import { saveLocalSound, updateLocalSound } from '../soundboard/soundStore';
import { MAX_UPLOAD_BYTES, sniffAudioFile, uploadWithProgress } from '../soundboard/soundUpload';

const field = { background: '#111214', color: '#fff', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '6px 8px' };

function errText(e, fallback) {
  return String((e && e.data && e.data.error) || (e && e.message) || fallback);
}

async function postClip(path, fields, blob, onProgress) {
  const file = blob instanceof File ? blob : new File([blob], 'clip.wav', { type: blob.type || 'audio/wav' });
  const r = await uploadWithProgress(path, fields, file, onProgress);
  if (r.status !== 200) throw new Error((r.data && r.data.error) || 'Upload failed');
  return r.data;
}

export function Waveform({ samples, rate, start, end }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c || !samples) return;
    const ctx2 = c.getContext('2d');
    const w = c.width;
    const h = c.height;
    ctx2.clearRect(0, 0, w, h);
    const p = peaks(samples, w);
    const dur = samples.length / rate;
    for (let x = 0; x < w; x++) {
      const t = (x / w) * dur;
      ctx2.fillStyle = t >= start && t <= end ? '#8db4ff' : 'rgba(255,255,255,0.25)';
      const bh = Math.max(1, p[x] * h);
      ctx2.fillRect(x, (h - bh) / 2, 1, bh);
    }
  }, [samples, rate, start, end]);
  return <canvas ref={ref} width={420} height={60} style={{ width: '100%', height: 60, background: '#111214', borderRadius: 8 }} />;
}

// collections: list from GET /sounds/collections (only editable ones are offered as targets).
// defaultTarget: 'local' or a collection id. allowLocal: offer "Save on this device".
// replace: { kind: 'local' | 'col', sound } to re-trim an existing sound in place.
export default function SoundClipEditor({ collections, onSaved, onCancel, defaultTarget = 'local', allowLocal = true, replace = null }) {
  const [decoded, setDecoded] = useState(null);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const [name, setName] = useState(replace ? replace.sound.name || '' : '');
  const [emoji, setEmoji] = useState(replace ? replace.sound.emoji || '' : '');
  const [description, setDescription] = useState('');
  const [keywords, setKeywords] = useState('');
  const [source, setSource] = useState(null); // the original MP3 file, when it can be uploaded as it is
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(replace && replace.sound.volume != null ? Number(replace.sound.volume) : 1);
  const [target, setTarget] = useState(String(defaultTarget));
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [rec, setRec] = useState(null);
  const [recSecs, setRecSecs] = useState(0);
  const previewRef = useRef(null);

  async function load(blob, defaultName) {
    setErr('');
    try {
      const isMp3 = blob instanceof File && blob.size <= MAX_UPLOAD_BYTES && (await sniffAudioFile(blob).catch(() => '')) === 'mp3';
      setSource(isMp3 ? blob : null);
      const d = await decodeAudioFile(blob);
      setDecoded(d);
      setStart(0);
      setEnd(Math.min(d.duration, MAX_CLIP_SECONDS));
      if (!name) setName(defaultName || 'New sound');
    } catch (e) {
      setErr(errText(e, 'Could not read that audio.'));
    }
  }

  // Re-trim: start from the sound's current audio.
  const replaceSound = replace && replace.sound;
  const replaceKind = replace && replace.kind;
  useEffect(() => {
    if (!replaceSound) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const blob = replaceKind === 'local' ? replaceSound.wav : await fetch(replaceSound.url, { credentials: 'include' }).then((r) => {
          if (!r.ok) throw new Error('Could not download the sound.');
          return r.blob();
        });
        const d = await decodeAudioFile(blob);
        if (cancelled) return;
        setDecoded(d);
        setStart(0);
        setEnd(Math.min(d.duration, MAX_CLIP_SECONDS));
      } catch (e) {
        if (!cancelled) setErr(errText(e, 'Could not read that audio.'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [replaceSound, replaceKind]);

  useEffect(() => () => previewRef.current && previewRef.current.pause(), []);

  async function startRecording() {
    setErr('');
    try {
      const r = await recordMicrophone(MAX_CLIP_SECONDS, { onTick: setRecSecs });
      setRec(r);
      const blob = await r.result;
      setRec(null);
      await load(blob, 'Recording'); // a recording is a Blob, so it is always sent as WAV
    } catch (e) {
      setRec(null);
      setErr(errText(e, 'Microphone recording failed.'));
    }
  }

  function preview() {
    if (!decoded) return;
    try {
      const wav = buildClip(decoded.samples, decoded.rate, start, end);
      const url = URL.createObjectURL(new Blob([wav], { type: 'audio/wav' }));
      if (previewRef.current) previewRef.current.pause();
      const a = new Audio(url);
      a.volume = volume;
      previewRef.current = a;
      a.onended = () => URL.revokeObjectURL(url);
      a.play().catch(() => {});
    } catch (e) {
      setErr(errText(e, 'Preview failed.'));
    }
  }

  async function save() {
    if (!decoded) return;
    setBusy(true);
    setErr('');
    try {
      const wav = buildClip(decoded.samples, decoded.rate, start, end);
      const blob = new Blob([wav], { type: 'audio/wav' });
      const meta = { name: name.trim() || 'Sound', emoji, volume };
      // An MP3 goes up unchanged with its trim range; everything else as the WAV made here.
      const trimmed = start > 0.005 || end < decoded.duration - 0.005;
      const upload = source || blob;
      const trim = source && trimmed ? { trim_start_ms: Math.round(start * 1000), trim_end_ms: Math.round(end * 1000) } : {};
      if (replace && replace.kind === 'local') {
        await updateLocalSound(replace.sound.id, { ...meta, wav: blob, localHash: await sha256Hex(wav), serverHash: '' });
      } else if (replace) {
        await postClip(`/sounds/sounds/${replace.sound.id}/audio`, trim, upload, setProgress);
        await comsApiPatch(`/sounds/sounds/${replace.sound.id}`, meta);
      } else if (target === 'local') {
        const localHash = await sha256Hex(wav);
        await saveLocalSound({ ...meta, wav: blob, localHash, serverHash: '' });
      } else {
        await postClip(`/sounds/collections/${target}/sounds`, { name: meta.name, emoji, volume: String(volume), description, keywords, ...trim }, upload, setProgress);
      }
      onSaved();
    } catch (e) {
      setErr(errText(e, 'Could not save the sound.'));
    } finally {
      setBusy(false);
    }
  }

  const duration = decoded ? decoded.duration : 0;
  const editable = (collections || []).filter((c) => c.can_edit);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 10, borderRadius: 10, border: '1px solid rgba(255,255,255,0.1)' }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <label className="coms-mini-btn" style={{ cursor: 'pointer' }}>
          {replace ? 'Replace with a file…' : 'Import audio file…'}
          <input type="file" accept="audio/*" style={{ display: 'none' }} onChange={(e) => e.target.files && e.target.files[0] && load(e.target.files[0], e.target.files[0].name.replace(/\.[^.]+$/, '').slice(0, 40))} />
        </label>
        {rec ? (
          <button type="button" className="coms-mini-btn" onClick={() => rec.stop()}>
            Stop recording ({recSecs.toFixed(1)} s)
          </button>
        ) : (
          <button type="button" className="coms-mini-btn" onClick={startRecording}>
            Record from microphone
          </button>
        )}
      </div>

      {decoded ? (
        <>
          <Waveform samples={decoded.samples} rate={decoded.rate} start={start} end={end} />
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 6, alignItems: 'center', fontSize: 12 }}>
            <span>Start</span>
            <input type="range" min={0} max={duration} step={0.01} value={start} aria-label="Trim start" onChange={(e) => setStart(Math.min(Number(e.target.value), end - 0.1))} />
            <span>{start.toFixed(2)} s</span>
            <span>End</span>
            <input type="range" min={0} max={duration} step={0.01} value={end} aria-label="Trim end" onChange={(e) => setEnd(Math.max(Number(e.target.value), start + 0.1))} />
            <span>{end.toFixed(2)} s</span>
          </div>
          <div style={{ fontSize: 12, color: end - start > MAX_CLIP_SECONDS ? '#ff8a8a' : 'rgba(255,255,255,0.7)' }}>
            Clip length {(end - start).toFixed(2)} s (max {MAX_CLIP_SECONDS} s)
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <input value={emoji} onChange={(e) => setEmoji(e.target.value.slice(0, 4))} placeholder="🔔" style={{ ...field, width: 50, textAlign: 'center' }} aria-label="Emoji" />
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" maxLength={40} style={{ ...field, flex: 1, minWidth: 120 }} aria-label="Sound name" />
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
              Volume
              <input type="range" min={0} max={1} step={0.05} value={volume} onChange={(e) => setVolume(Number(e.target.value))} />
            </label>
          </div>
          {!replace && target !== 'local' ? (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <input value={description} onChange={(e) => setDescription(e.target.value.slice(0, 200))} placeholder="Description (optional)" style={{ ...field, flex: '2 1 180px' }} aria-label="Description" />
              <input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="Search keywords" style={{ ...field, flex: '1 1 140px' }} aria-label="Search keywords" />
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {replace ? null : (
              <select value={target} onChange={(e) => setTarget(e.target.value)} style={field} aria-label="Save to">
                {allowLocal ? <option value="local">Save on this device</option> : null}
                {editable.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    Add to collection: {c.name}
                  </option>
                ))}
              </select>
            )}
            <button type="button" className="coms-mini-btn" onClick={preview}>
              Preview
            </button>
            <button type="button" className="coms-mini-btn primary" disabled={busy || end - start > MAX_CLIP_SECONDS} onClick={save}>
              {busy ? (progress > 0 && progress < 1 ? `Uploading ${Math.round(progress * 100)}%` : 'Saving…') : replace ? 'Save changes' : 'Save sound'}
            </button>
            <button type="button" className="coms-mini-btn" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </>
      ) : replace && !err ? (
        <div style={{ fontSize: 12, opacity: 0.7 }}>Loading the sound…</div>
      ) : null}
      {err ? <div className="coms-form-error">{err}</div> : null}
    </div>
  );
}
